import { FormData } from '../utils/validation';
import { ApprovalFlowStatus, SubmissionType } from '../types/submission';

// APIのレスポンス型定義
export type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
  statusCode?: number; // 409エラーなどのステータスコードを含める
};

// 古い ApplicationResponse 型の互換性のための型
export type LegacySubmissionDetailResponse = {
  id: string;
  division_id: string;
  company_id: string;
  department_name: string;
  applicant_name: string;
  email: string;
  phone?: string;
  position_id: string;
  employee_code: string;
  employment_type: string;
  purpose: string;
  always_on: boolean;
  always_on_reason?: string;
  vm_type: string;
  node_nw_ops_permission: string;
  lte_nw_ops_permission: string;
  ip_ops_permission: string;
  clap_permission: string;
  status: string;
  created_at: string;
  updated_at: string;
};

// 現在のユーザー情報の型定義
export type CurrentUserInfo = {
  department_name: string;
  family_name: string;
  given_name: string;
  email: string;
  phone: string | null;
  position: string;
  name_code: string;
  type_of_usage: string;
  usage_purpose: string;
  always_on: boolean;
  always_on_reason: string | null;
  vm_type: string;
  nops_permission: string;
  lte_ops_permission: string;
  ip_ops_permission: string;
  clap_permission: string;
};

// 新しいSubmissionDetailResponse型 - 新しいAPIレスポンス形式に合わせて更新
export type SubmissionDetailResponse = {
  id: string;
  display_id: string;
  status: string;
  submission_type?: string;
  division_id?: string;
  submitter_id?: string;
  family_name: string;
  given_name: string;
  department_name?: string;
  email: string;
  phone?: string;
  position: string;
  name_code: string;
  type_of_usage: string;
  usage_purpose: string;
  always_on: boolean;
  always_on_reason?: string;
  vm_type: string;
  nops_permission: string;
  lte_ops_permission: string;
  ip_ops_permission: string;
  clap_permission: string;
  created_at: string;
  updated_at: string;
  // 変更申請の場合の現在の情報
  now_info?: CurrentUserInfo;
  // OPAMユーザー申請の役割情報
  role?: string;
  role_type?: string;
  // 統合アカウント対応
  selected_services?: string[];
  ms365_required?: boolean;
  airnas_enabled?: boolean;
  // 後方互換性のために古いフィールド名も許容
  company_id?: string;
  applicant_name?: string;
  position_id?: string;
  employee_code?: string;
  employment_type?: string;
  purpose?: string;
  node_nw_ops_permission?: string;
  lte_nw_ops_permission?: string;
  // 異動リクエスト由来かどうか
  is_from_transfer_request?: boolean;
};

// 型定義を保持（実際のAPIエンドポイントは使用しない）
export type CompanyResponse = {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
};

export type DivisionResponse = {
  id: string;
  name: string;
  company_name: string;
  code: string;
  mailing_list: string;
};

export type DivisionsResponse = {
  divisions: DivisionResponse[];
};

export type PositionResponse = {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
};

export type UserResponse = {
  id: string;
  upn: string;
  family_name: string;
  given_name: string;
  name_code: string;
  email: string;
  phone?: string;
  department_name: string;
  position: string;
  type_of_usage: string;
  usage_purpose: string;
  always_on: boolean;
  always_on_reason?: string;
  vm_type: string;
  ip_address: string;
  nops_permission: string;
  lte_ops_permission: string;
  ip_ops_permission: string;
  clap_permission: string;
  division: {
    id: string;
    name: string;
    company_name: string;
    code: string;
    mailing_list: string;
  };
  // 統合アカウント対応
  selected_services?: string[];
  ms365_required?: boolean;
  role?: string;
  role_type?: string;
  // 利用方式（Pools / Personal, 複数選択可）
  usage_method?: string[] | null;
  // 下位互換性のために残す
  company?: string | null;
  company_id?: string | null;
  position_id?: string | null;
  employee_code?: string | null;
  is_organization_admin?: boolean;
  is_superuser?: boolean;
  created_at?: string;
  updated_at?: string;
};

export type UserListResponse = UserResponse & {
  // 旧形式のfieldのエイリアス
  division_id?: string;
  company?: string | null;
  company_id?: string | null;
  position_id?: string | null;
  employee_code?: string | null;
  is_organization_admin?: boolean;
  is_superuser?: boolean;
  created_at?: string;
  updated_at?: string;
  // 統合アカウントフォーム対応
  selected_systems?: string[];  // ["AirOPE", "OPAM", "AirNAS"]
  ms365_required?: boolean;  // APIレスポンスと一致
  airnas_enabled?: boolean;
};

export type SubmissionResponse = {
  id: string;
  status: ApprovalFlowStatus;
  display_id: string;
  submission_type: SubmissionType;
  submitter_name: string;
  division_name: string;
  actor_name: string | null;
  created_at: string;
  updated_at: string;
  // 統合アカウントフォーム対応
  selected_services?: string[];  // ["AirOPE", "OPAM", "AirNAS"]
  ms365_required?: boolean;  // APIレスポンスと一致
  ms365_reason?: string | null;
  airnas_enabled?: boolean;
};

export type SubmissionsResponse = {
  submissions: SubmissionResponse[];
};

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:8000/api/v1';
  }

  // 旧APIで申請データを送信 (廃止予定)
  async submitApplication(data: FormData): Promise<ApiResponse<SubmissionDetailResponse>> {
    try {
      // 新しい方法に移行
      return this.submitNewSubmission(data);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 新しい命名規則に合わせたメソッド
  async submitSubmission(data: FormData): Promise<ApiResponse<SubmissionDetailResponse>> {
    return this.submitNewSubmission(data);
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    // 401 Unauthorized エラーの処理
    if (response.status === 401) {
      // セッション切れを検知
      console.log('[ApiClient] Session expired, redirecting to login...');

      // 現在のURLを保存
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname + window.location.search;

        // すでにルートページにいる場合はリダイレクトしない
        if (currentPath === '/' || currentPath === '') {
          throw new Error('認証が必要です。ログインしてください。');
        }

        sessionStorage.setItem('redirectUrl', currentPath);

        // WID132対策: キャッシュをクリア（LocalStorage + SessionStorage）
        localStorage.removeItem('opam_user');
        localStorage.removeItem('opam_isAuthenticated');
        localStorage.removeItem('opam_cache_timestamp');

        // WID132対策: SessionStorageからも削除
        sessionStorage.removeItem('opam_user_min');
        sessionStorage.removeItem('opam_auth_state');
        sessionStorage.removeItem('opam_cache_timestamp');

        // ログインページへリダイレクト
        window.location.href = '/';
      }

      throw new Error('セッションが切れました。再度ログインしてください。');
    }

    if (!response.ok) {
      try {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || '通信エラーが発生しました');
      } catch (e) {
        if (e instanceof Error) {
          throw e;
        }
        throw new Error(`${response.status}: ${response.statusText || '通信エラーが発生しました'}`);
      }
    }

    try {
      // 実際のレスポンスを取得
      const data = await response.json();

      // 標準的なレスポンス形式の場合
      if (data.success !== undefined) {
        return data as ApiResponse<T>;
      }

      // APIがラップしていないデータを直接返す場合
      return {
        success: true,
        data: data as T
      };
    } catch (error) {
      // JSON解析エラーや空のレスポンスの場合
      return {
        success: true,
        data: {} as T
      };
    }
  }

  // ログイン中のユーザー情報を取得
  async getCurrentUser(): Promise<ApiResponse<UserResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/whoami`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include'
      });
      return this.handleResponse<UserResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 新しいエンドポイントで申請データを送信
  async submitNewSubmission(data: any): Promise<ApiResponse<any>> {
    try {
      // データの形式をチェックして適切に変換
      let transformedData: any;

      // すでに適切な形式の場合はそのまま使用
      if (data && data.family_name && data.given_name) {
        transformedData = data;
      } else {
        // 旧形式のデータを新しい形式に変換
        transformedData = {
          department_name: data.department_name || "",
          family_name: data.applicant_name?.split(' ')[0] || data.family_name || "",
          given_name: data.applicant_name?.split(' ')[1] || data.given_name || "",
          email: data.email || "",
          phone: data.phone || null,
          position: data.position_id || data.position || "",
          name_code: data.employee_code || data.name_code || "",
          usage_purpose: data.purpose || data.usage_purpose || "",
          always_on: data.always_on || false,
          always_on_reason: data.always_on_reason || null,
          vm_type: data.vm_type || "",
          nops_permission: data.node_nw_ops_permission || data.nops_permission || "none",
          lte_ops_permission: data.lte_nw_ops_permission || data.lte_ops_permission || "none",
          ip_ops_permission: data.ip_ops_permission || "none",
          clap_permission: data.clap_permission || "none"
        };
      }

      const response = await fetch(`${this.baseUrl}/submissions/new`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(transformedData),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.detail || errorJson.message || `エラー: ${response.status} ${response.statusText}`);
        } catch (e) {
          throw new Error(`エラー: ${response.status} ${response.statusText} - ${errorText}`);
        }
      }

      return this.handleResponse<any>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 申請一覧を取得 (廃止予定)
  async getApplications(): Promise<ApiResponse<SubmissionDetailResponse[]>> {
    try {
      // 新しいAPIを呼び出して変換
      const result = await this.getSubmissions();

      // 旧形式に変換 (実際には実装されていない、互換性のために残す)
      return {
        success: result.success,
        data: result.data as unknown as SubmissionDetailResponse[],
        error: result.error
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 特定の申請を取得
  async getSubmission(id: string): Promise<ApiResponse<SubmissionDetailResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`申請データの取得に失敗しました: ${response.status} ${response.statusText}`);
      }

      const result = await this.handleResponse<SubmissionDetailResponse>(response);
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 削除申請を取得
  async getDeleteSubmission(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/${id}/delete`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`削除申請データの取得に失敗しました: ${response.status} ${response.statusText}`);
      }

      const result = await this.handleResponse<any>(response);
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // バリデーションエラーメッセージを抽出する関数
  private extractValidationErrorMessages(errorData: any): string {
    // FastAPIのバリデーションエラー形式をチェック
    if (errorData.detail && Array.isArray(errorData.detail)) {
      const messages = errorData.detail.map((error: any) => {
        if (error.msg) {
          return error.msg;
        }
        if (error.message) {
          return error.message;
        }
        return 'バリデーションエラーが発生しました';
      });
      return messages.join('\n');
    }

    // 単一のエラーメッセージの場合
    if (typeof errorData.detail === 'string') {
      return errorData.detail;
    }

    if (errorData.message) {
      return errorData.message;
    }

    return 'エラーが発生しました';
  }

  // 申請を更新
  async updateSubmission(id: string, data: any): Promise<ApiResponse<SubmissionDetailResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      // 409エラー（変更点がありません）を特別にハンドリング
      if (response.status === 409) {
        const errorData = await response.json();
        return {
          success: false,
          error: this.extractValidationErrorMessages(errorData) || '変更点がありません',
          statusCode: 409
        };
      }

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = this.extractValidationErrorMessages(errorData);
        return {
          success: false,
          error: errorMessage || `申請データの更新に失敗しました: ${response.status} ${response.statusText}`,
          statusCode: response.status
        };
      }

      const result = await this.handleResponse<SubmissionDetailResponse>(response);
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 申請を変更する（新しいエンドポイント）
  async changeSubmission(data: any): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.detail || errorJson.message || `エラー: ${response.status} ${response.statusText}`);
        } catch (e) {
          throw new Error(`エラー: ${response.status} ${response.statusText} - ${errorText}`);
        }
      }

      return this.handleResponse<any>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 申請をキャンセル
  async cancelSubmission(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });
      return this.handleResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 申請を承認
  async approveSubmission(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/${id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include'
      });
      return this.handleResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 申請を否認
  async rejectSubmission(id: string, comment: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/${id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ comment })
      });
      return this.handleResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 申請を実行
  async executeSubmission(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/${id}/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include'
      });
      return this.handleResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 削除申請を完了
  async completeDeleteSubmission(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/${id}/complete-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include'
      });
      return this.handleResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 部署一覧を取得
  async getDivisions(): Promise<ApiResponse<DivisionResponse[]>> {
    try {
      const response = await fetch(`${this.baseUrl}/divisions`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include'
      });

      const result = await this.handleResponse<DivisionsResponse>(response);

      // レスポンス形式がdivisionsキーを持つオブジェクトの場合、divisionsの値を返す
      if (result.success && result.data && 'divisions' in result.data) {
        return {
          success: true,
          data: result.data.divisions
        };
      }

      return result as unknown as ApiResponse<DivisionResponse[]>;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 特定の部署情報を取得
  async getDivision(divisionId: string): Promise<ApiResponse<DivisionResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/divisions/${divisionId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include'
      });

      return this.handleResponse<DivisionResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // ユーザー一覧を取得
  async getUsers(
    divisionId?: string,
    filters?: Record<string, string>,
    offset: number = 0,
    limit: number = 10,
    search?: string
  ): Promise<ApiResponse<{ users: UserListResponse[], total_count: number }>> {
    try {
      const queryParams = new URLSearchParams();

      // ページネーションパラメータを追加
      queryParams.append('offset', offset.toString());
      queryParams.append('limit', limit.toString());

      // 組織IDを追加
      if (divisionId) {
        queryParams.append('division_id', divisionId);
      }

      // 検索キーワードを追加
      if (search) {
        queryParams.append('search', search);
      }

      // フィルタリングパラメータがあれば追加
      if (filters && Object.keys(filters).length > 0) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value) {
            queryParams.append(key, value);
          }
        });
      }

      const url = `${this.baseUrl}/users/?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include'
      });
      const result = await this.handleResponse<{ users: UserResponse[], total_count: number }>(response);

      // APIレスポンスをそのまま返す
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // ユーザー一覧のフィルターオプションを取得
  async getUserFilterOptions(divisionId?: string): Promise<ApiResponse<{ department_names: string[], user_names: string[] }>> {
    try {
      const queryParams = new URLSearchParams();
      if (divisionId) {
        queryParams.append('division_id', divisionId);
      }

      const url = `${this.baseUrl}/users/filter-options${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include'
      });

      return await this.handleResponse<{ department_names: string[], user_names: string[] }>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // ユーザーを削除する
  async deleteUser(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${this.baseUrl}/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include'
      });
      return this.handleResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 削除申請を作成する
  async createDeleteSubmission(userId: string, reason: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: userId,
          reason: reason
        })
      });
      return this.handleResponse<any>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 申請一覧を取得するメソッド
  async getSubmissions(): Promise<ApiResponse<SubmissionResponse[]>> {
    try {
      // 通信タイムアウト用のPromise
      const timeoutPromise = new Promise<Response>((_, reject) => {
        setTimeout(() => reject(new Error('タイムアウトしました')), 10000); // 10秒タイムアウト
      });

      // 実際のAPIリクエスト
      const fetchPromise = fetch(`${this.baseUrl}/submissions/?offset=0&limit=100`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include'
      });

      try {
        // どちらか早い方を採用
        const response = await Promise.race([fetchPromise, timeoutPromise]) as Response;

        if (!response.ok) {
          throw new Error(`APIエラー: ${response.status} ${response.statusText}`);
        }

        const rawResponse = await response.json();

        // 応答が正しい形式かチェック - submissionsキーがある場合
        if (rawResponse && rawResponse.submissions && Array.isArray(rawResponse.submissions)) {
          // レスポンスの各項目が必要なプロパティを持っているか簡易チェック
          const isValidFormat = rawResponse.submissions.every((submission: any) => {
            return (
              submission.id &&
              submission.display_id &&
              submission.submission_type &&
              submission.status
            );
          });

          if (isValidFormat) {
            return {
              success: true,
              data: rawResponse.submissions
            };
          }
        }
        // submissionsキーがない場合でも、配列だと思われる場合は直接使用する
        else if (Array.isArray(rawResponse)) {
          const isValidFormat = rawResponse.every((submission: any) => {
            return (
              submission.id &&
              submission.display_id &&
              submission.submission_type &&
              submission.status
            );
          });

          if (isValidFormat) {
            return {
              success: true,
              data: rawResponse
            };
          }
        }

        return {
          success: false,
          error: '不正なレスポンス形式です',
        };
      } catch (fetchError) {
        throw fetchError; // 再スロー
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }


  // 承認待ち申請一覧を取得
  async getToDoSubmissions(offset = 0, limit = 100): Promise<ApiResponse<SubmissionsResponse>> {
    try {
      const url = `${this.baseUrl}/submissions/to-do?offset=${offset}&limit=${limit}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`APIエラー: ${response.status} ${response.statusText}`);
      }

      const result = await this.handleResponse<SubmissionsResponse>(response);
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }
  // OPAMユーザー新規申請の更新
  async updateOpamUserNewSubmission(submissionId: string, data: {
    family_name: string;
    given_name: string;
    name_code: string;
    position: string;
    email: string;
    role: string;
    role_type?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const url = `${this.baseUrl}/submissions/opam-user-new/${submissionId}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        // エラーレスポンスの詳細メッセージを取得
        let errorMessage = `APIエラー: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
          console.warn('エラーレスポンスのパースに失敗:', parseError);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'OPAMユーザー新規申請の更新でエラーが発生しました',
      };
    }
  }

  // OPAMユーザー登録申請の役割重複チェック
  async checkOpamRoleDuplication(role: string, divisionId?: string): Promise<ApiResponse<{ isAvailable: boolean; message?: string }>> {
    try {
      const url = `${this.baseUrl}/opam/check-role`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          role,
          division_id: divisionId
        })
      });

      if (!response.ok) {
        throw new Error(`APIエラー: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '役割重複チェックでエラーが発生しました',
      };
    }
  }

  // 申請検索（事務局向け）
  async searchSubmissions(params: {
    display_id?: string;
    email?: string;
    family_name?: string;
    given_name?: string;
    offset?: number;
    limit?: number;
  }): Promise<ApiResponse<SubmissionSearchResponse>> {
    try {
      const queryParams = new URLSearchParams();
      if (params.display_id) queryParams.append('display_id', params.display_id);
      if (params.email) queryParams.append('email', params.email);
      if (params.family_name) queryParams.append('family_name', params.family_name);
      if (params.given_name) queryParams.append('given_name', params.given_name);
      queryParams.append('offset', String(params.offset || 0));
      queryParams.append('limit', String(params.limit || 20));

      const response = await fetch(`${this.baseUrl}/submissions/search?${queryParams.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `APIエラー: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '申請検索でエラーが発生しました',
      };
    }
  }

  // === 異動リクエスト関連 ===

  // 異動リクエスト用ユーザー検索
  async searchUsersForTransfer(q: string, page: number = 1, perPage: number = 10): Promise<ApiResponse<TransferUserSearchResponse>> {
    try {
      const params = new URLSearchParams();
      params.append('q', q);
      params.append('page', page.toString());
      params.append('per_page', perPage.toString());

      const response = await fetch(`${this.baseUrl}/users/search?${params.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      return this.handleResponse<TransferUserSearchResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 異動リクエスト一覧取得
  async getTransferRequests(params: {
    tab?: string;
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<ApiResponse<TransferRequestListResponse>> {
    try {
      const queryParams = new URLSearchParams();
      if (params.tab) queryParams.append('tab', params.tab);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      queryParams.append('page', String(params.page || 1));
      queryParams.append('per_page', String(params.per_page || 20));

      const response = await fetch(`${this.baseUrl}/transfer-requests/?${queryParams.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      return this.handleResponse<TransferRequestListResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 異動リクエスト詳細取得
  async getTransferRequestDetail(requestId: string): Promise<ApiResponse<TransferRequestDetailResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/transfer-requests/${requestId}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      return this.handleResponse<TransferRequestDetailResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 異動リクエスト一括作成
  async createTransferRequests(data: {
    to_division_id: string;
    requests: Array<{
      user_id: string;
      transfer_date: string;
      assignee_name: string;
      comment?: string | null;
    }>;
  }): Promise<ApiResponse<{ created_count: number; request_ids: string[] }>> {
    try {
      const response = await fetch(`${this.baseUrl}/transfer-requests/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      return this.handleResponse<{ created_count: number; request_ids: string[] }>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 異動リクエスト削除
  async deleteTransferRequest(requestId: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${this.baseUrl}/transfer-requests/${requestId}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });

      // 204 No Content の場合
      if (response.status === 204) {
        return { success: true, data: undefined };
      }
      return this.handleResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 異動リクエスト一括削除
  async bulkDeleteTransferRequests(ids: string[]): Promise<ApiResponse<{ deleted_count: number }>> {
    try {
      const response = await fetch(`${this.baseUrl}/transfer-requests/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ ids }),
      });
      return this.handleResponse<{ deleted_count: number }>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 異動リクエスト重複チェック
  async checkTransferRequestDuplicates(
    userIds: string[],
    toDivisionId: string,
  ): Promise<ApiResponse<{ duplicates: { user_id: string; name: string }[] }>> {
    try {
      const response = await fetch(`${this.baseUrl}/transfer-requests/check-duplicates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ user_ids: userIds, to_division_id: toDivisionId }),
      });
      return this.handleResponse<{ duplicates: { user_id: string; name: string }[] }>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 異動リクエスト承認
  async approveTransferRequest(requestId: string, comment?: string): Promise<ApiResponse<TransferRequestDetailResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/transfer-requests/${requestId}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ comment: comment || null }),
      });
      return this.handleResponse<TransferRequestDetailResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 異動リクエスト否認
  async rejectTransferRequest(requestId: string, reason: string): Promise<ApiResponse<TransferRequestDetailResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/transfer-requests/${requestId}/reject`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ reason }),
      });
      return this.handleResponse<TransferRequestDetailResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '予期せぬエラーが発生しました',
      };
    }
  }

  // 事務局によるステータス変更
  async updateSubmissionStatusByOffice(
    submissionId: string,
    status: string,
    comment?: string
  ): Promise<ApiResponse<{ id: string; status: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/submissions/${submissionId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ status, comment }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `APIエラー: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'ステータス変更でエラーが発生しました',
      };
    }
  }
}

// 異動リクエスト用ユーザー検索レスポンス型
export type TransferUserSearchItem = {
  id: string;
  name: string;
  email: string;
  company_name: string;
  organization_name: string;
  division_id: string;
};

export type TransferUserSearchResponse = {
  items: TransferUserSearchItem[];
  total: number;
  page: number;
  per_page: number;
};

// 異動リクエスト一覧レスポンス型
export type TransferRequestListItemResponse = {
  id: string;
  user_id: string;
  target_user_name: string;
  email: string;
  organization_code: string;
  source_company_name: string;
  source_organization_name: string;
  to_division_name: string;
  to_company_name: string;
  requester_name: string;
  requester_company_name: string;
  requester_division_name: string;
  transfer_date: string;
  new_department_name: string;
  status: string;
  request_date: string | null;
};

export type TransferRequestListResponse = {
  items: TransferRequestListItemResponse[];
  total: number;
  page: number;
  per_page: number;
};

// 異動リクエスト詳細レスポンス型
export type TransferRequestDetailResponse = {
  id: string;
  user_id: string;
  user_name: string;
  email: string;
  user_upn: string;
  from_division_id: string;
  current_organization: string;
  organization_code: string;
  to_division_id: string;
  to_division_name: string;
  to_company_name: string;
  requester_id: string;
  requester_name: string;
  requester_company_name: string;
  requester_division_name: string;
  transfer_date: string;
  new_department_name: string;
  remarks: string | null;
  status: string;
  reviewer_id: string | null;
  reviewer_name: string | null;
  reviewer_comment: string | null;
  reviewed_at: string | null;
  submission_id: string | null;
  submission_display_id: string | null;
  request_date: string | null;
  updated_at: string | null;
  is_approver: boolean;
};

// 申請検索レスポンス型
export type SubmissionSearchResponse = {
  submissions: SubmissionPreviewResponse[];
  total_count: number;
};

// 申請プレビューレスポンス型
export type SubmissionPreviewResponse = {
  id: string;
  display_id: string;
  status: string;
  submission_type: string;
  submitter_name: string;
  division_name: string;
  company_name: string;
  created_at: string;
  updated_at: string;
  selected_services: string[];
  actor_name?: string;
};

export const apiClient = new ApiClient();
